import type { Cell, ChessShapeName, ShapeName } from './types';

export const CHESS_SHAPES: ChessShapeName[] = ['pawn', 'rook', 'knight', 'bishop', 'queen', 'king'];
export function isChessShape(shape: ShapeName): shape is ChessShapeName {
  return (CHESS_SHAPES as ShapeName[]).includes(shape);
}
type Point = readonly [number, number];
function polygon(x: number, y: number, points: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i]!, [xj, yj] = points[j]!;
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
const stem: readonly Point[] = [[.37, .4], [.63, .4], [.61, .58], [.69, .72], [.79, .8], [.21, .8], [.31, .72], [.39, .58]];
const rook: readonly Point[] = [[.18, .05], [.32, .05], [.32, .17], [.43, .17], [.43, .05], [.57, .05], [.57, .17], [.68, .17], [.68, .05], [.82, .05], [.82, .32], [.67, .39], [.64, .69], [.77, .8], [.23, .8], [.36, .69], [.33, .39], [.18, .32]];
const knight: readonly Point[] = [[.58, .04], [.72, .13], [.81, .3], [.84, .49], [.77, .69], [.79, .8], [.23, .8], [.29, .68], [.42, .54], [.53, .42], [.46, .34], [.32, .42], [.13, .35], [.17, .23], [.35, .14], [.43, .03], [.48, .13], [.57, .16]];
const queen: readonly Point[] = [[.13, .12], [.31, .24], [.32, .05], [.45, .21], [.5, .01], [.55, .21], [.68, .05], [.69, .24], [.87, .12], [.74, .4], [.26, .4]];
const bishop: readonly Point[] = [[.5, .01], [.75, .22], [.78, .31], [.68, .4], [.32, .4], [.22, .31], [.25, .22]];
function contains(shape: ChessShapeName, x: number, y: number): boolean {
  // Shared stepped plinth keeps every silhouette recognizable when filled with paths.
  if (y >= .9 && x >= .06 && x <= .94) return true;
  if (y >= .8 && x >= .16 && x <= .84) return true;
  if (shape === 'rook') return polygon(x, y, rook);
  if (shape === 'knight') return polygon(x, y, knight);
  if (polygon(x, y, stem)) return true;
  if (y >= .4 && y <= .48 && x >= .27 && x <= .73) return true;
  if (shape === 'pawn') return ((x - .5) / .22) ** 2 + ((y - .23) / .2) ** 2 <= 1;
  if (shape === 'queen') return polygon(x, y, queen);
  if (shape === 'bishop') {
    // A notch open to the edge suggests the bishop's diagonal mitre cut.
    const slit = y < .29 && x > .5 && Math.abs(y - (.63 - x) * .8 - .12) < .035;
    return polygon(x, y, bishop) && !slit;
  }
  // King: cross, crown, then the shared tapered body.
  return (x >= .4 && x <= .6 && y < .27) ||
    (x >= .25 && x <= .75 && y >= .08 && y < .16) ||
    polygon(x, y, [[.24, .26], [.76, .26], [.69, .4], [.31, .4]]);
}
export function chessCells(width: number, height: number, shape: ChessShapeName): Cell[] {
  const cells: Cell[] = [];
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (contains(shape, (x + .5) / width, (y + .5) / height)) cells.push({ x, y });
  }
  // Tiny crown tips can alias to isolated pixels at low resolution. Omit those
  // pixels instead of presenting impossible one-cell arrows to the generator.
  const keys = new Set(cells.map(c => `${c.x},${c.y}`));
  return cells.filter(c => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => keys.has(`${c.x + dx!},${c.y + dy!}`)));
}
